/**
 * @file     panelRegistry.ts
 * @readme   ../../CLAUDE.md
 * @purpose  Single source of truth for the insights panel band: order,
 *           skeleton heights, and premium-gate config. Consumed by the slug
 *           page (DetailTabsSection) and the quick views (QuickViewBody,
 *           through TheAreaCardClient). The six former area
 *           panels (market story, market demand, area mix, housing stock,
 *           demographics, supply outlook) are merged into ONE segmented
 *           "The area" card; its segment layout lives in AREA_SEGMENTS.
 *           Reordering panels or editing teaser copy happens here and
 *           nowhere else.
 */

export type ReadBucket = 'smoking_hot' | 'worth_a_look' | 'fair_value' | 'skip';

// Benchmark-relative, signal-framed labels — compliance-safe wording (never a
// verdict on another brokerage's listing). Used by the Read panel summary.
export const READ_BUCKET_LABEL: Record<ReadBucket, string> = {
  smoking_hot: 'Strong value signals',
  worth_a_look: 'Good value signals',
  fair_value: 'In line with market',
  skip: 'Weak value signals',
};

export interface PanelPremiumConfig {
  panel: 'my_read' | 'supply_outlook' | 'demographics';
  teaserTitle: string;
  teaserBody: string;
  treatment?: 'panel' | 'values';
}

export type AreaPanelKey =
  | 'marketStory'
  | 'marketDemand'
  | 'areaMix'
  | 'housingStock'
  | 'demographics'
  | 'supplyOutlook';

export interface AreaSegmentEntry {
  key: 'market' | 'homes' | 'people' | 'supply';
  label: string;
  /** Panels composed inside this segment, in render order. The segment is
   *  dropped when none of its panels resolved data. */
  panels: readonly AreaPanelKey[];
  premium?: PanelPremiumConfig;
}

/** Segment layout of the merged "The area" card. */
export const AREA_SEGMENTS: readonly AreaSegmentEntry[] = [
  { key: 'market', label: 'Market', panels: ['marketStory', 'marketDemand'] },
  { key: 'homes', label: 'Homes', panels: ['areaMix', 'housingStock'] },
  {
    key: 'people',
    label: 'People',
    panels: ['demographics'],
    premium: {
      panel: 'demographics',
      teaserTitle: 'See who lives here',
      teaserBody: 'StatCan dissemination-area Census Profile: income, age, education, housing, commute.',
      treatment: 'values',
    },
  },
  {
    key: 'supply',
    label: 'Supply',
    panels: ['supplyOutlook'],
    premium: {
      panel: 'supply_outlook',
      teaserTitle: 'See the Supply Outlook',
      teaserBody: 'CMHC scenarios: 2035 prices, starts gap, stock growth, ground vs apartment pace.',
    },
  },
] as const;

export interface InsightPanelEntry {
  key: 'theArea' | 'homeValue' | 'myRead';
  /** Skeleton height for map slide-up loading states. Omitted for VOW sections. */
  skeletonHeight?: number;
  premium?: PanelPremiumConfig;
  /** VOW-gated section: sandbox-only, wrapped in VowSandboxGate when access is missing. */
  vow?: boolean;
}

export const INSIGHT_PANELS: readonly InsightPanelEntry[] = [
  { key: 'theArea', skeletonHeight: 420 },
  { key: 'homeValue', vow: true },
  {
    key: 'myRead',
    skeletonHeight: 520,
    premium: {
      panel: 'my_read',
      teaserTitle: 'See how the price compares',
      teaserBody:
        'A score for buyers and investors, built from nine signals.',
    },
  },
] as const;
